import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Gamepad2, Film, MessageSquare, Upload, User, LogOut, Menu, X } from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navLinks = [
    { to: '/vortex', label: 'THE VORTEX', icon: null },
    { to: '/games', label: 'GAMES', icon: Gamepad2 },
    { to: '/videos', label: 'MOVIES', icon: Film },
    { to: '/bbs', label: 'BBS', icon: MessageSquare },
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <header className="ws-header sticky top-0 z-50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-14">
          {/* Logo */}
          <Link to="/" className="ws-logo flex items-center" data-testid="nav-logo">
            <span className="ws-logo-war">WAR</span>
            <span className="ws-logo-syndicate">SYNDICATE</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1" data-testid="nav-desktop">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`ws-nav-link flex items-center gap-1 ${isActive(to) ? 'active' : ''}`}
                data-testid={`nav-link-${label.toLowerCase().replace(' ', '-')}`}
              >
                {Icon && <Icon size={14} />}
                {label}
              </Link>
            ))}
          </nav>

          {/* User Section */}
          <div className="flex items-center gap-2">
            {user ? (
              <>
                <Link
                  to="/upload"
                  className="btn-primary hidden sm:flex items-center gap-1 text-xs py-1.5 px-3"
                  data-testid="nav-upload-btn"
                >
                  <Upload size={14} />
                  UPLOAD
                </Link>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button 
                      className="flex items-center gap-2 p-1.5 hover:bg-white/10 transition-colors"
                      data-testid="nav-user-menu"
                    >
                      {user.avatar_url ? (
                        <img 
                          src={user.avatar_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}` : user.avatar_url} 
                          alt="" 
                          className="w-8 h-8 border border-[#333]"
                        />
                      ) : (
                        <div className="w-8 h-8 bg-[#1a1a1a] border border-[#333] flex items-center justify-center">
                          <User size={16} className="text-[#666]" />
                        </div>
                      )}
                      <span className="text-xs text-[#999] hidden sm:block">
                        {user.username || user.name}
                      </span>
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-[#0a0a0a] border-[#333] min-w-[150px]">
                    <DropdownMenuItem asChild>
                      <Link 
                        to={`/user/${user.user_id}`} 
                        className="flex items-center gap-2 text-[#999] hover:text-white cursor-pointer"
                        data-testid="nav-profile-link"
                      >
                        <User size={14} />
                        MY PROFILE
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={logout}
                      className="flex items-center gap-2 text-[#cc0000] hover:text-[#ff3333] cursor-pointer"
                      data-testid="nav-logout-btn"
                    >
                      <LogOut size={14} />
                      LOGOUT
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <div className="flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="btn-secondary text-xs py-1.5 px-3"
                  data-testid="nav-login-btn"
                >
                  LOGIN
                </Link>
                <Link 
                  to="/register" 
                  className="btn-primary text-xs py-1.5 px-3"
                  data-testid="nav-register-btn"
                >
                  SIGN UP
                </Link>
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button 
              className="md:hidden p-2 text-[#999] hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="md:hidden py-4 border-t border-[#222]" data-testid="nav-mobile">
            {navLinks.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className={`ws-nav-link flex items-center gap-2 py-3 ${isActive(to) ? 'active' : ''}`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {Icon && <Icon size={16} />}
                {label}
              </Link>
            ))}
            {user && (
              <Link
                to="/upload"
                className="ws-nav-link flex items-center gap-2 py-3"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Upload size={16} />
                UPLOAD
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
};
